import React from 'react';
import { Chip, IconButton, Typography } from '@sm-dashboard/ui-components';
import { Card, CardContent, CardMedia, Box, Tooltip, useMediaQuery } from '@mui/material';
import { WhatsApp, Favorite, FavoriteBorder, Comment, Phone, Edit } from '@mui/icons-material';
import { BusinessAd, getImageUrl, handleWhatsAppClick, hasWhatsApp, RatingData } from './businessAdsUtils';
import { theme } from '@/theme/theme';
import { useLayoutEffect, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import PhoneModal from './PhoneModal';
import defaultAdImage from '@/assets/chabad-board-default.png';

interface BusinessAdCardProps {
  ad: BusinessAd;
  isFavorited: boolean;
  commentCount: number;
  ratingData: RatingData;
  onToggleFavorite: (adId: string) => void;
  onCardClick: (ad: BusinessAd) => void;
  onEdit?: (ad: BusinessAd) => void;
}

const BusinessAdCard = ({
  ad,
  isFavorited,
  commentCount,
  ratingData,
  onToggleFavorite,
  onCardClick,
  onEdit
}: BusinessAdCardProps) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [phoneModalOpen, setPhoneModalOpen] = useState(false);

  const businessName = ad.businessName;
  const phone = ad.businessPhone;
  const isWhatsAppAvailable = hasWhatsApp(ad);

  const adChips = [
    ...(ad.category ? [{ label: ad.category, variant: 'primary' as const }] : []),
    ...(ad.targetAudience || []).map(audience => ({ label: audience, variant: 'info' as const }))
  ];

  const CHIP_GAP_PX = 4;
  const chipsMeasureRef = useRef<HTMLDivElement>(null);
  const chipsContainerRef = useRef<HTMLDivElement>(null);
  const [visibleAdChipsCount, setVisibleAdChipsCount] = useState(adChips.length);

  useLayoutEffect(() => {
    const container = chipsContainerRef.current;
    const measureRoot = chipsMeasureRef.current;
    if (!container || !measureRoot) {
      return;
    }

    if (adChips.length === 0) {
      setVisibleAdChipsCount(0);
      return;
    }

    const measure = () => {
      const containerWidth = container.clientWidth;
      if (containerWidth <= 0) {
        return;
      }

      const chipNodes = measureRoot.querySelectorAll<HTMLElement>('[data-ad-chip]');
      const overflowNode = measureRoot.querySelector<HTMLElement>('[data-ad-chip-overflow]');
      if (chipNodes.length === 0) {
        return;
      }

      const overflowWidth = overflowNode?.offsetWidth ?? 0;
      let usedWidth = 0;
      let count = 0;

      for (let i = 0; i < chipNodes.length; i++) {
        const chipWidth = chipNodes[i].offsetWidth;
        const gapBefore = count > 0 ? CHIP_GAP_PX : 0;
        const remaining = chipNodes.length - i - 1;
        const reserveOverflow = remaining > 0 ? CHIP_GAP_PX + overflowWidth : 0;

        if (usedWidth + gapBefore + chipWidth + reserveOverflow <= containerWidth) {
          usedWidth += gapBefore + chipWidth;
          count++;
        } else {
          break;
        }
      }

      setVisibleAdChipsCount(prev => (prev !== count ? count : prev));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [adChips.length]);

  const hiddenAdChipsCount = adChips.length - visibleAdChipsCount;

  const handleContactClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (isMobile) {
      // Mobile - direct action
      ReactGA.event({
        category: 'BusinessAdWa',
        action: 'לחיצה על ווצאפ',
        label: ad._id
      });
      handleWhatsAppClick(ad, true);
    } else {
      // Desktop - show modal
      setPhoneModalOpen(true);
    }
  };

  const handleWhatsAppAction = () => {
    const message = `שלום, ראיתי את המודעה שלכם עבור ${businessName} במערכת חב"ד ישראל`;
    const whatsappUrl = `https://wa.me/972${phone.replace(/^0/, '')}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handlePhoneAction = () => {
    const phoneUrl = `tel:${phone}`;
    window.open(phoneUrl, '_self');
  };

  return (
    <Card
      sx={{
        display: 'flex',
        bgcolor: '#ffffff',
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        mb: 2,
        minHeight: '165px',
        width: { xs: '100%' },
        mx: 'auto',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 2
        }
      }}
      onClick={() => onCardClick(ad)}
    >
      <Box sx={{ flex: '0 0 33.33%', maxWidth: '33.33%', position: 'relative', p: 1 }}>
        <CardMedia
          component="img"
          sx={{
            width: '100%',
            height: '100%',
            minHeight: 145,
            objectFit: 'cover',
            borderRadius: 1
          }}
          image={getImageUrl(ad) || defaultAdImage}
          alt={ad.businessName}
        />

        {/* Discount Badge */}
        {ad.shabadHouseDiscount && (
          <Box sx={{ position: 'absolute', top: 16, left: 16, zIndex: 1 }}>
            <Chip label="הנחה לבתי חב״ד" size="small" variant="success" />
          </Box>
        )}
      </Box>

      <CardContent
        sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', p: 1.5 }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0 }}>
          <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
            <Typography variant="h5" noWrap>
              {ad.businessName}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 1, flexShrink: 0 }}>
            {onEdit && (
              <Tooltip title="עריכה">
                <IconButton
                  shape="circle"
                  variant="text"
                  color="secondary"
                  size="medium"
                  aria-label="עריכה"
                  onClick={e => {
                    e.stopPropagation();
                    onEdit(ad);
                  }}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <Comment fontSize="small" color="action" />
              <Box sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>{commentCount}</Box>
            </Box>

            <Tooltip title={isFavorited ? 'הסר ממועדפים' : 'הוסף למועדפים'}>
              <IconButton
                shape="circle"
                variant={isFavorited ? 'contained' : 'text'}
                color={isFavorited ? 'error' : 'secondary'}
                size="medium"
                aria-label={isFavorited ? 'הסר ממועדפים' : 'הוסף למועדפים'}
                onClick={e => {
                  e.stopPropagation();
                  onToggleFavorite(ad._id);
                }}
              >
                {isFavorited ? <Favorite fontSize="small" /> : <FavoriteBorder fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Box
            sx={{
              color: 'text.secondary',
              mb: 0.25,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              fontSize: '0.8rem',
              lineHeight: 1.2
            }}
          >
            {ad.shortDescription}
          </Box>

          {(ad.minPrice || ad.maxPrice) && (
            <Box
              sx={{
                color: 'text.secondary',
                mb: 0.3,
                fontSize: '0.8rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: { xs: '250px', md: '300px' }
              }}
            >
              <strong>מחיר:</strong>{' '}
              {ad.minPrice && ad.maxPrice
                ? `₪${ad.minPrice} - ₪${ad.maxPrice}`
                : ad.minPrice
                ? `החל מ-₪${ad.minPrice}`
                : `עד ₪${ad.maxPrice}`}
            </Box>
          )}

          <Box
            sx={{
              color: 'text.secondary',
              mb: 0.3,
              fontSize: '0.8rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: { xs: '250px', md: '300px' }
            }}
          >
            <strong>מיקום:</strong> {ad.location?.join(', ') || '-'}
          </Box>
        </Box>

        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'flex-end', gap: 1 }}>
          {/* Hidden measuring row — same chips, used only to compute how many fit */}
          <Box
            ref={chipsMeasureRef}
            aria-hidden
            sx={{
              position: 'absolute',
              visibility: 'hidden',
              pointerEvents: 'none',
              height: 0,
              overflow: 'hidden',
              display: 'flex',
              gap: 0.5
            }}
          >
            {adChips.map((chip, index) => (
              <Box key={`${chip.label}-${index}`} data-ad-chip>
                <Chip label={chip.label} size="small" variant={chip.variant} />
              </Box>
            ))}
            <Box data-ad-chip-overflow>
              <Chip label={`+${adChips.length}`} size="small" variant="neutral" />
            </Box>
          </Box>

          <Box
            ref={chipsContainerRef}
            sx={{
              flex: 1,
              display: 'flex',
              gap: 0.5,
              flexWrap: 'nowrap',
              overflow: 'hidden',
              alignItems: 'baseline',
              minWidth: 0
            }}
          >
            {adChips.slice(0, visibleAdChipsCount).map((chip, index) => (
              <Chip key={`${chip.label}-${index}`} label={chip.label} size="small" variant={chip.variant} />
            ))}
            {hiddenAdChipsCount > 0 && (
              <Chip label={`+${hiddenAdChipsCount}`} size="small" variant="neutral" />
            )}
          </Box>

          <Box sx={{ flexShrink: 0 }}>
            <Tooltip title={isWhatsAppAvailable ? 'שלח הודעת WhatsApp' : 'התקשר'}>
              <IconButton
                shape="circle"
                color={isWhatsAppAvailable ? 'success' : 'primary'}
                size="medium"
                aria-label={isWhatsAppAvailable ? 'שלח הודעת WhatsApp' : 'התקשר'}
                onClick={handleContactClick}
              >
                {isWhatsAppAvailable ? <WhatsApp fontSize="small" /> : <Phone fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </CardContent>

      <PhoneModal
        open={phoneModalOpen}
        onClose={() => setPhoneModalOpen(false)}
        businessName={businessName}
        phoneNumber={phone}
        hasWhatsApp={isWhatsAppAvailable}
        onWhatsAppClick={handleWhatsAppAction}
        onPhoneClick={handlePhoneAction}
        adId={ad._id}
      />
    </Card>
  );
};

export default React.memo(BusinessAdCard);
